import Sheet from "components/sheet/Sheet";
import React, { useState } from "react";
import { Select } from "components/select/index";
import { IDiscount } from "api/rpc/2022-09/facilityAdmin/product/discount";
import Input from "components/form/input";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Checkbox from "components/form/checkbox/Checkbox";
import { useTranslation } from "react-i18next";
import TextField from "components/form/textField/TextField";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import "./discountLineItemModal.scss";

interface IDiscountLineItemModalProps {
  open: boolean;
  title: string;
  supportText?: string;
  onCancel: () => void;
  onOk: (customAmount?: string, typeFixed?: boolean) => void;
  onChange: (id: number, discount: IDiscount) => void;
  okDisabled: boolean;
  discounts: IDiscount[];
  selectedDiscount?: IDiscount;
  noteDiscount?: string;
  changeNoteState?: (e: React.ChangeEvent) => void;
}
interface IState {
  customAmount: string;
  /**typeFixed = true to display fixed amount input, else display percent input */
  typeFixed: boolean;
  showNoteInput: boolean;
}

export default function DiscountLineItemModal(props: IDiscountLineItemModalProps) {
  const {
    open,
    title,
    supportText,
    onCancel,
    onOk,
    onChange,
    okDisabled,
    discounts,
    selectedDiscount,
    noteDiscount = "",
    changeNoteState,
  } = props;
  const [state, setState] = useState<IState>({
    customAmount: "",
    typeFixed: true,
    showNoteInput: false,
  });

  const { Option } = Select;
  const { t } = useTranslation();

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    event.stopPropagation();
    const { id, value } = event.target;
    setState(prevState => ({ ...prevState, [id]: value === "true" ? true : false }));
  }

  function handleOnCancel() {
    setState(prevState => ({ ...prevState, customAmount: "", typeFixed: true, showNoteInput: false }));
    void onCancel();
  }

  function handleOnOk() {
    if (selectedDiscount?.custom) {
      void onOk(state.customAmount, state.typeFixed);
    } else {
      void onOk();
    }

    setState(prevState => ({ ...prevState, customAmount: "", typeFixed: true, showNoteInput: false }));
  }

  // Stop Enter key from refreshing page
  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleOnOk();
    }
  }

  const fieldEmpty = selectedDiscount?.custom === true && state.customAmount === "" ? true : false;

  return (
    <>
      {open && (
        <Sheet
          size="small"
          title={title}
          supportText={supportText}
          open={open}
          onCancel={handleOnCancel}
          onOk={handleOnOk}
          okText={t("Apply discount")}
          closable
          cancelText={t("Go back")}
          okDisabled={okDisabled || fieldEmpty}
          overflow
        >
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Select
                  label={t("Discount")}
                  className="flex justify-center align-center w-full h-10 position-relative z-20 text-black text-medium appearance-none border-none focus:outline-none placeholder-gray-200"
                  placeholder={t("Select a discount...")}
                  onChange={onChange}
                >
                  {discounts?.length > 0 &&
                    discounts
                      .filter(discount => discount.application === "line_item")
                      .map(discount => {
                        return (
                          <Option key={discount.id} value={discount.id} extraValues={discount}>
                            {discount.title}
                          </Option>
                        );
                      })}
                </Select>
              </FormLayout.Group>

              {selectedDiscount?.custom && (
                <>
                  <FormLayout.Group>
                    <div
                      className={classNames("loyalty-checkbox-container", {
                        "loyalty-checkbox-container-active": state.typeFixed,
                      })}
                      onClick={() => setState(prev => ({ ...prev, typeFixed: true }))}
                    >
                      <Checkbox
                        id="typeFixed"
                        size="medium"
                        value="true"
                        checked={state.typeFixed}
                        onChange={handleCheckboxChange}
                        label={t("Fixed Amount")}
                        circle
                      />
                    </div>
                    <div
                      className={classNames("loyalty-checkbox-container", {
                        "loyalty-checkbox-container-active": !state.typeFixed,
                      })}
                      onClick={() => setState(prev => ({ ...prev, typeFixed: false }))}
                    >
                      <Checkbox
                        id="typeFixed"
                        size="medium"
                        value="false"
                        checked={!state.typeFixed}
                        onChange={handleCheckboxChange}
                        label={t("Percent")}
                        circle
                      />
                    </div>
                  </FormLayout.Group>
                  <FormLayout.Group>
                    {state.typeFixed ? (
                      <Input
                        type="number"
                        label={t("Fixed Amount")}
                        prefix="$"
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        id="customAmount"
                        value={state.customAmount}
                        placeholder="0.00"
                      />
                    ) : (
                      <Input
                        type="number"
                        label={t("Percent")}
                        suffix="%"
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        id="customAmount"
                        value={state.customAmount}
                        placeholder="0"
                      />
                    )}
                  </FormLayout.Group>
                </>
              )}
            </FormLayout>
          </Form>

          <div className="discount-line-item-modal-divider" />

          {state.showNoteInput ? (
            <TextField
              label={t("Note")}
              value={noteDiscount}
              onChange={changeNoteState}
              placeholder={t("Add note for discount...")}
              maxLength={100}
              characterCountBottom
            />
          ) : (
            <Button
              type="text"
              size="small"
              className="discount-line-item-modal-add-note"
              onClick={() => setState(prev => ({ ...prev, showNoteInput: true }))}
              disabled={!changeNoteState}
            >
              <FontAwesomeIcon icon={faPlus} />
              {t("Add note")}
            </Button>
          )}
        </Sheet>
      )}
    </>
  );
}
